import React, { useState, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
const getStyles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: `${theme.spacing.xs} 0`,
    fontSize: theme.fontSize.sm,
    color: theme.colors.text.secondary,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '4px 0',
    lineHeight: '1.4',
    transition: 'opacity 0.3s ease',
  },
  stepCompleted: {
    opacity: 0.6,
  },
  stepIcon: {
    flexShrink: 0,
    width: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    marginTop: '1px',
  },
  stepContent: {
    flex: 1,
    minWidth: 0,
  },
  stepLabel: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.text.secondary,
  },
  stepLabelActive: {
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeight.medium,
  },
  stepDetail: {
    fontSize: theme.fontSize.xs,
    color: theme.colors.text.muted,
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  stepDetailExpanded: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  stepDetailCollapsed: {
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  expandToggle: {
    background: 'none',
    border: 'none',
    color: theme.colors.LUMO,
    fontSize: theme.fontSize.xs,
    cursor: 'pointer',
    padding: 0,
    marginTop: '2px',
    textDecoration: 'underline',
  },
  spinner: {
    width: '12px',
    height: '12px',
    border: `2px solid ${theme.colors.SHELL}`,
    borderTopColor: theme.colors.LUMO,
    borderRadius: '50%',
    animation: 'activitySpin 0.8s linear infinite',
  },
  checkmark: {
    color: theme.colors.GEO,
    fontSize: '13px',
    fontWeight: theme.fontWeight.bold,
  },
  errorIcon: {
    color: theme.colors.danger,
    fontSize: '13px',
  },
  functionName: {
    fontFamily: theme.fontFamily.monospace,
    fontSize: theme.fontSize.xs,
    backgroundColor: theme.colors.background.modal,
    padding: '1px 4px',
    borderRadius: theme.borderRadius.sm,
  },
});

const keyframesId = 'agent-activity-feed-keyframes';

const injectKeyframes = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(keyframesId)) return;
  const style = document.createElement('style');
  style.id = keyframesId;
  style.textContent = `
    @keyframes activitySpin {
      to { transform: rotate(360deg); }
    }
    @keyframes activityFadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
};

const StepIcon = ({ status }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (status === 'completed') {
    return <span style={styles.checkmark}>&#10003;</span>;
  }
  if (status === 'error') {
    return <span style={styles.errorIcon}>&#10007;</span>;
  }
  return <div style={styles.spinner} />;
};

const ActivityStep = ({ step }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [expanded, setExpanded] = useState(false);
  const isActive = step.status === 'active';
  const isCompleted = step.status === 'completed';
  const hasDetail = step.detail && step.detail.length > 0;
  const isLongDetail = hasDetail && step.detail.length > 80;

  return (
    <div
      style={{
        ...styles.step,
        ...(isCompleted ? styles.stepCompleted : {}),
        animation: 'activityFadeIn 0.25s ease-out',
      }}
    >
      <div style={styles.stepIcon}>
        <StepIcon status={step.status} />
      </div>
      <div style={styles.stepContent}>
        <div style={{ ...styles.stepLabel, ...(isActive ? styles.stepLabelActive : {}) }}>{step.label}</div>
        {hasDetail && (
          <div
            style={{
              ...styles.stepDetail,
              ...(expanded ? styles.stepDetailExpanded : styles.stepDetailCollapsed),
            }}
          >
            {expanded ? step.detail : isLongDetail ? step.detail.slice(0, 80) + '...' : step.detail}
          </div>
        )}
        {isLongDetail && (
          <button style={styles.expandToggle} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

const AgentActivityFeed = ({ steps = [], compact = false }) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  injectKeyframes();

  if (!steps || steps.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.step}>
          <div style={styles.stepIcon}>
            <div style={styles.spinner} />
          </div>
          <div style={{ ...styles.stepLabel, ...styles.stepLabelActive }}>Thinking...</div>
        </div>
      </div>
    );
  }

  const displaySteps = compact ? steps.slice(-3) : steps;

  return (
    <div style={styles.container}>
      {displaySteps.map((step) => (
        <ActivityStep key={step.id} step={step} />
      ))}
    </div>
  );
};

export default AgentActivityFeed;
